/**
 * User Management Utilities
 * Helper functions for filtering, sorting, and formatting
 */

import { User, FilterState, SortConfig } from './userManagementTypes';
import { format, parseISO } from 'date-fns';

// ─── Search Function ──────────────────────────────────────────────────────────
export const searchUsers = (users: User[], searchTerm: string): User[] => {
  if (!searchTerm.trim()) return users;
  
  const term = searchTerm.toLowerCase();
  return users.filter(user => {
    const email = String(user.userEmail ?? '').toLowerCase();
    const gpid = String(user.gpid ?? '').toLowerCase();
    const locationId = String(user.locationId ?? '').toLowerCase();
    const locations = Array.isArray(user.location)
      ? user.location.map(loc => String(loc ?? '').toLowerCase())
      : [];

    return (
      email.includes(term) ||
      gpid.includes(term) ||
      locationId.includes(term) ||
      locations.some(loc => loc.includes(term))
    );
  });
};

// ─── Filter Function ──────────────────────────────────────────────────────────
export const filterUsers = (users: User[], filters: FilterState): User[] => {
  let filtered = [...users];

  // Search filter
  if (filters.search.trim()) {
    filtered = searchUsers(filtered, filters.search);
  }

  // Access Level filter
  if (filters.accessLevel.length > 0) {
    filtered = filtered.filter(user => 
      filters.accessLevel.includes(user.accessLevel)
    );
  }

  // Location filter
  if (filters.location.length > 0) {
    filtered = filtered.filter(user => 
      user.location.some(loc => filters.location.includes(loc))
    );
  }

  // Active Status filter
  if (filters.activeStatus.length > 0) {
    const filterValues = filters.activeStatus.map(s => s.toLowerCase().trim());
    filtered = filtered.filter(user =>
      filterValues.includes((user.activeStatus ?? '').toLowerCase().trim())
    );
  }

  // Sustain Team Member filter
  if (filters.sustainTeamMember.length > 0) {
    filtered = filtered.filter(user => {
      const isSustain = user.sustainTeamMember === true;
      if (filters.sustainTeamMember.includes('Yes') && isSustain) return true;
      if (filters.sustainTeamMember.includes('No') && !isSustain) return true;
      return false;
    });
  }

  // New Status filter
  if (filters.newStatus !== null) {
    filtered = filtered.filter(user => user.newStatus === filters.newStatus);
  }

  return filtered;
};

// ─── Sort Function ────────────────────────────────────────────────────────────
export const sortUsers = (users: User[], sortConfig: SortConfig): User[] => {
  if (!sortConfig.column) return users;

  const sorted = [...users];
  const { column, direction } = sortConfig;

  sorted.sort((a, b) => {
    const aValue = a[column];
    const bValue = b[column];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return direction === 'asc' ? 1 : -1;
    if (bValue === null || bValue === undefined) return direction === 'asc' ? -1 : 1;

    // Handle date strings
    if (column === 'naviJoinDate' || column === 'lastUpdateTime' ||
        column === 'fundamentals' || column === 'lighthouse' || column === 'advFundamentals') {
      const dateA = new Date(aValue as string).getTime();
      const dateB = new Date(bValue as string).getTime();
      return direction === 'asc' ? dateA - dateB : dateB - dateA;
    }

    // Handle arrays (location)
    if (Array.isArray(aValue) && Array.isArray(bValue)) {
      const aStr = aValue.join(', ');
      const bStr = bValue.join(', ');
      return direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    }

    // Handle strings
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Handle numbers
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // Handle booleans
    if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      return direction === 'asc' 
        ? (aValue === bValue ? 0 : aValue ? 1 : -1)
        : (aValue === bValue ? 0 : aValue ? -1 : 1);
    }

    return 0;
  });

  return sorted;
};

// ─── Filter and Sort Combined ─────────────────────────────────────────────────
export const filterAndSortUsers = (
  users: User[],
  filters: FilterState,
  sortConfig: SortConfig
): User[] => {
  const filtered = filterUsers(users, filters);
  return sortUsers(filtered, sortConfig);
};

// ─── Date Formatting ──────────────────────────────────────────────────────────
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '—';
  
  try {
    const date = parseISO(dateString);
    return format(date, 'MM/dd/yyyy');
  } catch (error) {
    return '—';
  }
};

export const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '—';
  
  try {
    const date = parseISO(dateString);
    return format(date, 'MM/dd/yyyy HH:mm');
  } catch (error) {
    return '—';
  }
};

// ─── Validation ───────────────────────────────────────────────────────────────
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

export const isFutureDate = (dateString: string): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date > today;
};

/**
 * Convert a Date object to YYYY-MM-DD string in local timezone
 * Avoids timezone issues with toISOString()
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ─── Get Unique Values for Filters ────────────────────────────────────────────
export const getUniqueAccessLevels = (users: User[]): string[] => {
  const levels = new Set(users.map(u => u.accessLevel));
  return Array.from(levels).sort();
};

export const getUniqueLocations = (users: User[]): string[] => {
  const locations = new Set(users.flatMap(u => u.location));
  return Array.from(locations).sort();
};

// ─── Count Utilities ──────────────────────────────────────────────────────────
export const countOverwrittenTrainingDates = (
  users: User[],
  selectedIds: string[],
  trainingField: 'fundamentals' | 'lighthouse' | 'advFundamentals'
): number => {
  return users
    .filter(u => selectedIds.includes(u.id))
    .filter(u => u[trainingField] !== null)
    .length;
};

export const countNewUsers = (users: User[]): number => {
  return users.filter(u => u.newStatus).length;
};

export const countActiveUsers = (users: User[]): number => {
  return users.filter(u => u.activeStatus === 'Active').length;
};

export const countInactiveUsers = (users: User[]): number => {
  return users.filter(u => u.activeStatus === 'Inactive').length;
};

// ─── Check if Filters are Active ──────────────────────────────────────────────
export const hasActiveFilters = (filters: FilterState): boolean => {
  return (
    filters.search !== '' ||
    filters.accessLevel.length > 0 ||
    filters.location.length > 0 ||
    filters.activeStatus.length > 0 ||
    filters.sustainTeamMember.length > 0 ||
    filters.newStatus !== null
  );
};
